import React, { useState, useEffect } from 'react';
import { Upload, Sparkles, Share2, QrCode, Download, Play, FileText, Mic, Wand2, Layers, Zap, ArrowRight, Link, Globe, Smartphone } from 'lucide-react';
import './LandingPage.css';


const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      title: "AI Prompt to QR Code",
      description: "Simply describe what you need and get a custom QR code instantly",
      icon: <Sparkles className="feature-icon-large" />
    },
    {
      title: "Static QR Codes",
      description: "Generate permanent QR codes for URLs, text, contacts, and more",
      icon: <QrCode className="feature-icon-large" />
    },
    {
      title: "Dynamic QR Codes",
      description: "Create editable QR codes with tracking and analytics capabilities",
      icon: <Globe className="feature-icon-large" />
    },
    {
      title: "All Formats Supported",
      description: "WiFi, vCard, SMS, Email, Location, and 20+ other formats",
      icon: <Layers className="feature-icon-large" />
    }
  ];

  const methods = [
    {
      icon: <Wand2 className="method-icon" />,
      title: "AI Prompt",
      desc: "Just describe your QR code need",
      color: "violet-purple"
    },
    {
      icon: <Link className="method-icon" />,
      title: "URL & Links",
      desc: "Websites and social media",
      color: "blue-cyan"
    },
    {
      icon: <Smartphone className="method-icon" />,
      title: "Contact Info",
      desc: "vCard, phone, email",
      color: "orange-red"
    },
    {
      icon: <Globe className="method-icon" />,
      title: "WiFi & More",
      desc: "WiFi, location, payments",
      color: "green-emerald"
    }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-container-split">
          {/* Left Side - Content */}
          <div className="hero-left">
            
            <h1 className="hero-title">
              Generate QR Codes From
              <span className="title-highlight">Any Prompt</span>
            </h1>
            
            <p className="hero-subtitle">
              No forms. No complexity. Just describe what you need and get your QR code instantly. Static or dynamic, all formats supported.
            </p>
            
            <div className="hero-actions">
              <button 
                className="btn-primary-landing"
                onClick={() => window.location.href = "/qrgenerate"}
              >
                <Sparkles className="btn-icon" />
                Start Creating
                <ArrowRight className="btn-icon-arrow" />
              </button>
              <button className="btn-secondary">
                <Play className="btn-icon" />
                Watch Demo
              </button>
            </div>

            
          </div>
          
          {/* Right Side - Animated Demo */}
          <div className="hero-right">
                <div className="demo-content">
                 
                    <Wand2 className="prompt-icon" />
                    <div className="typing-box">
                    <div className="typing-animation">
                      <span className="typing-text">{
                        currentFeature === 0 ? "Create a QR code for my WiFi..." :
                        currentFeature === 1 ? "Generate vCard for John Doe..." :
                        currentFeature === 2 ? "Make QR for payment link..." :
                        "Create menu QR code..."
                      }<span className="cursor">|</span>
                      
                      </span>
                      
                    </div>
                    </div>
            
                  
                  
                  <div className="qr-output">
                    <div className="qr-preview">
                      <QrCode className="qr-icon" size={80} />
                      <div className="scan-line"></div>
                    </div>
                    <div className="output-label">
                      <div className="success-badge">
                        <span className="success-dot"></span>
                        Generated Successfully
                      </div>
                      
                </div>
              </div>
              
              {/* Floating Elements */}
          
            </div>
          </div>
        </div>
      </section>

      {/* Generation Methods */}
      <section className="methods-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">All QR Code Types</h2>
            <p className="section-subtitle">Every format you need, generated instantly</p>
          </div>
          
          <div className="methods-grid">
            {methods.map((method, idx) => (
              <div key={idx} className="method-card">
                <div className={`method-icon-wrapper ${method.color}`}>
                  {method.icon}
                </div>
                <h3 className="method-title">{method.title}</h3>
                <p className="method-desc">{method.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rotating Features */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            <div className="features-left">
              <h2 className="section-title">Everything You Need</h2>
              <div className="features-list">
                {features.map((feature, idx) => (
                  <div 
                    key={idx}
                    className={`feature-item ${currentFeature === idx ? 'active' : ''}`}
                  >
                    <div className="feature-icon-container">
                      {feature.icon}
                    </div>
                    <div className="feature-text">
                      <h3 className="feature-title">{feature.title}</h3>
                      <p className="feature-desc">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="features-right">
              <div className="features-visual">
                <div className="visual-item">
                  <div className="visual-header">
                    <QrCode className="visual-icon" />
                    <span className="visual-text">25+ QR Code Formats</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '100%'}}></div>
                  </div>
                </div>
                <div className="visual-item">
                  <div className="visual-header">
                    <Zap className="visual-icon" />
                    <span className="visual-text">Instant Generation</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '100%'}}></div>
                  </div>
                </div>
                <div className="visual-item">
                  <div className="visual-header">
                    <Download className="visual-icon" />
                    <span className="visual-text">High-Res Download (PNG, SVG)</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{width: '90%'}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Three simple steps to your perfect QR code</p>
          </div>
          
          <div className="steps-grid">
            <div className="step">
              <div className="step-icon-wrapper">
                <Wand2 className="step-icon" />
              </div>
              <h3 className="step-title">Describe Your Need</h3>
              <p className="step-desc">Type what you want in plain English</p>
            </div>
            <div className="step">
              <div className="step-icon-wrapper">
                <Sparkles className="step-icon" />
              </div>
              <h3 className="step-title">AI Generates</h3>
              <p className="step-desc">Instantly creates your custom QR code</p>
            </div>
            <div className="step">
              <div className="step-icon-wrapper">
                <Download className="step-icon" />
              </div>
              <h3 className="step-title">Download & Use</h3>
              <p className="step-desc">Get high-resolution files instantly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Perfect For Everyone</h2>
            <p className="section-subtitle">From businesses to personal use, QR codes made simple</p>
          </div>
          
          <div className="use-cases-grid">
            {[
              { title: "Businesses", desc: "Product links, payment QR codes, and customer reviews", emoji: "💼" },
              { title: "Restaurants", desc: "Digital menus, WiFi access, and feedback forms", emoji: "🍽️" },
              { title: "Events", desc: "Tickets, check-ins, and event information", emoji: "🎟️" },
              { title: "Marketing", desc: "Campaign tracking, social media, and promotions", emoji: "📱" },
              { title: "Real Estate", desc: "Property listings, virtual tours, and contact info", emoji: "🏠" },
              { title: "Personal", desc: "vCards, WiFi sharing, and social profiles", emoji: "👤" }
            ].map((use, idx) => (
              <div key={idx} className="use-case-card">
                <div className="use-case-emoji">{use.emoji}</div>
                <h3 className="use-case-title">{use.title}</h3>
                <p className="use-case-desc">{use.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR Code Types Section */}
      <section className="methods-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Static & Dynamic QR Codes</h2>
            <p className="section-subtitle">Choose the right type for your needs</p>
          </div>
          
          <div className="methods-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: '800px', margin: '0 auto'}}>
            <div className="method-card">
              <div className="method-icon-wrapper violet-purple">
                <QrCode className="method-icon" />
              </div>
              <h3 className="method-title">Static QR Codes</h3>
              <p className="method-desc">Permanent codes that never expire. Perfect for printed materials and long-term use.</p>
              <ul style={{textAlign: 'left', marginTop: '1rem', fontSize: '0.9rem', color: '#666', lineHeight: '1.8'}}>
                <li>✓ Never expires</li>
                <li>✓ No scan limits</li>
                <li>✓ Works offline</li>
                <li>✓ Free forever</li>
              </ul>
            </div>
            <div className="method-card">
              <div className="method-icon-wrapper blue-cyan">
                <Globe className="method-icon" />
              </div>
              <h3 className="method-title">Dynamic QR Codes</h3>
              <p className="method-desc">Editable codes with analytics. Update content anytime without reprinting.</p>
              <ul style={{textAlign: 'left', marginTop: '1rem', fontSize: '0.9rem', color: '#666', lineHeight: '1.8'}}>
                <li>✓ Edit anytime</li>
                <li>✓ Track scans</li>
                <li>✓ Advanced analytics</li>
                <li>✓ A/B testing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      


{/* Pricing Section */}
       <section className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">Choose the plan that fits your needs</p>
          </div>
          
          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="pricing-title">Free</h3>
                <div className="pricing-price">
                  <span className="price-amount">$0</span>
                  <span className="price-period">Forever</span>
                </div>
                <p className="pricing-desc">Perfect for getting started</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 3 QR codes per month</li>
                <li>✓ 1 Dynamic QR code</li>
                <li>✓ Static QR support</li>
                <li>✓ Custom color and logo options</li>
                <li>✓ Download in PNG or SVG</li>
              </ul>
              <button 
                className="pricing-btn"
                onClick={() => window.location.href = "/qrgenerate"}
              >
                Start Free
              </button>
            </div>

            {/* Pro Plan */}
            <div className="pricing-card pricing-card-popular">
              <div className="popular-badge">Most Popular</div>
              <div className="pricing-header">
                <h3 className="pricing-title">Pro</h3>
                <div className="pricing-price">
                  <span className="price-amount">$6.99</span>
                  <span className="price-period">per month</span>
                </div>
                <p className="pricing-desc">For professionals and small teams</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 100 QR codes per month</li>
                <li>✓ Static QR codes</li>
                <li>✓ Dynamic & Trackable QR codes</li>
                <li>✓ 5 QR/hour limit</li>
                <li>✓ Download in PNG, SVG, or PDF</li>
              </ul>
              <button 
                className="pricing-btn pricing-btn-popular"
                onClick={() => window.location.href = "/pricing-plans"}
              >
                Visit Now
              </button>
            </div>

            {/* Premium Plan */}
            <div className="pricing-card">
              <div className="pricing-header">
                <h3 className="pricing-title">Premium</h3>
                <div className="pricing-price">
                  <span className="price-amount">$19.99</span>
                  <span className="price-period">per month</span>
                </div>
                <p className="pricing-desc">For businesses and agencies</p>
              </div>
              <ul className="pricing-features">
                <li>✓ 400 QR codes per month</li>
                <li>✓ Unlimited Dynamic QR codes</li>
                <li>✓ Advanced analytics and tracking</li>
                <li>✓ 5 QR/hour limit</li>
                <li>✓ Download in all formats</li>
              </ul>
              <button 
                className="pricing-btn"
                onClick={() => window.location.href = "/pricing-plans"}
              >
                Visit Now
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about our QR code generator</p>
          </div>
          
          <div className="faq-grid">
            {[
              {
                question: "What's the difference between static and dynamic QR codes?",
                answer: "Static QR codes contain fixed information that cannot be changed once generated. Dynamic QR codes use a redirect URL, allowing you to update the destination without changing the QR code itself."
              },
              {
                question: "Can I customize the appearance of my QR codes?",
                answer: "Yes! All plans include custom color options and logo integration. You can make your QR codes match your brand identity while maintaining scannability."
              },
              {
                question: "What file formats are available for download?",
                answer: "Free plan includes PNG and SVG formats. Pro plan adds PDF support. Premium plan includes all formats plus additional high-resolution options for professional printing."
              },
              {
                question: "Do QR codes expire?",
                answer: "Static QR codes never expire and work offline. Dynamic QR codes remain active as long as your subscription is valid. After cancellation, they redirect to a generic page."
              },
              {
                question: "Can I track how many people scan my QR codes?",
                answer: "Yes, with Pro and Premium plans. You'll get detailed analytics including scan counts, locations, devices, and time data to help you understand your audience."
              },
              {
                question: "What types of content can I encode in QR codes?",
                answer: "You can create QR codes for URLs, WiFi credentials, vCards, emails, phone numbers, SMS, locations, app stores, payments, and 20+ other formats."
              }
            ].map((faq, idx) => (
              <div key={idx} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Create Your QR Code?</h2>
          <p className="cta-subtitle">
            No forms to fill. No technical knowledge needed. Just prompt and generate.
          </p>
          <button 
            className="btn-cta"
            onClick={() => window.location.href = "/generate"}
          >
            <Sparkles className="btn-icon" />
            Generate Your First QR Code
            <ArrowRight className="btn-icon" />
          </button>
          <p className="cta-note">No credit card required • Unlimited static QR codes free</p>
        </div>
      </section>







    </div>
  );
};

export default LandingPage;